import { useEffect, useId, useState } from 'react';
import { AuthApiError } from '../account/authApi';
import { Button } from '../components/Button/Button';
import { useClaimUsername } from './useProfile';
import './ClaimUsernameDialog.css';

export interface ClaimUsernameDialogProps {
  isOpen: boolean;
  /** Optional initial value (e.g. derived from email). */
  initialValue?: string;
  /** What URL to surface as the example, e.g. "bryta.app". */
  hostHint?: string;
  onClose: () => void;
  onClaimed?: (username: string) => void;
}

/**
 * Dialog that asks the kid to claim a username. Used in two places:
 *  - first-signin nudge on the studio Home (`#claim-username` hash)
 *  - manual edit later from the AccountChip menu
 *
 * Validation matches `app.usernames.validate`. We mirror the rules
 * client-side so the kid sees instant feedback while typing, but the
 * server is still the authority — a 422 / 409 from the server is
 * surfaced verbatim.
 */
const CLIENT_RE = /^[a-z0-9](?:[a-z0-9_-]{1,28}[a-z0-9])$/;

function clientValidate(raw: string): string | null {
  const v = raw.trim().toLowerCase();
  if (v.length < 3 || v.length > 30) return 'Between 3 and 30 letters/numbers.';
  if (!CLIENT_RE.test(v))
    return 'Lowercase letters, numbers, hyphen or underscore. Start and end with a letter or number.';
  if (/--|__|-_|_-/.test(v)) return 'No double dashes or underscores.';
  return null;
}

export function ClaimUsernameDialog({
  isOpen,
  initialValue,
  hostHint = 'brytaapps.com',
  onClose,
  onClaimed,
}: ClaimUsernameDialogProps): JSX.Element | null {
  const titleId = useId();
  const inputId = useId();
  const claim = useClaimUsername();
  const [value, setValue] = useState(initialValue ?? '');
  const [showLocalError, setShowLocalError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setValue(initialValue ?? '');
      setShowLocalError(false);
      claim.reset();
      return;
    }
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !claim.isPending) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialValue]);

  if (!isOpen) return null;

  const trimmed = value.trim().toLowerCase();
  const localError = showLocalError ? clientValidate(trimmed) : null;
  const serverError =
    claim.error instanceof AuthApiError
      ? claim.error.detail || claim.error.message
      : claim.error?.message;
  const error = serverError || localError;

  function submit(): void {
    const v = value.trim().toLowerCase();
    setShowLocalError(true);
    if (clientValidate(v) !== null) return;
    claim.mutate(
      { username: v },
      {
        onSuccess: (result) => {
          onClaimed?.(result.username);
          onClose();
        },
      },
    );
  }

  return (
    <div
      className="bl-claim__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !claim.isPending) onClose();
      }}
    >
      <div className="bl-claim__dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 id={titleId} className="bl-claim__title">
          Pick a username
        </h2>
        <p className="bl-claim__lead">
          People can find your work at{' '}
          <code className="bl-claim__host">
            {hostHint}/u/{trimmed || 'your-name'}
          </code>
          . You can change it later.
        </p>
        <form
          className="bl-claim__form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label htmlFor={inputId} className="bl-claim__label">
            Username
          </label>
          <input
            id={inputId}
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (showLocalError) setShowLocalError(false);
              claim.reset();
            }}
            placeholder="alice"
            autoComplete="off"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- modal dialog opens on explicit user action; first input gets focus
            autoFocus
            disabled={claim.isPending}
            className="bl-claim__input"
            maxLength={30}
            spellCheck={false}
          />
          <p className="bl-claim__rules">3–30 chars · lowercase · letters, numbers, - or _</p>
          {error && (
            <p className="bl-claim__error" role="alert">
              {error}
            </p>
          )}
          <div className="bl-claim__actions">
            <Button variant="secondary" type="button" onClick={onClose} disabled={claim.isPending}>
              Not now
            </Button>
            <Button type="submit" disabled={claim.isPending || !trimmed}>
              {claim.isPending ? 'Saving…' : 'Claim it'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
